import React, {createContext, useContext, useMemo} from "react";
import {
    GetUniversityByIdQuery,
    GetUniversityBySlugQuery,
    useGetUniversityByIdQuery,
    useGetUniversityBySlugQuery,
} from "../types/generated";
import {Heading, View} from "@unibuddy/patron";
import {ApolloError} from "@apollo/client";

export type UniversityContextType = {
    universityId: string;
    universityName: string;
    universitySlug: string;
    widgetUrl?: string;
};

const UniversityContext = createContext<UniversityContextType>({
    universityId: "",
    universityName: "",
    universitySlug: "",
    widgetUrl: undefined,
});

export function useUniversityContext() {
    return useContext(UniversityContext);
}

type UniversityProviderProps = {
    universityId: string | null;
    universitySlug: string | null;
    children: React.ReactNode;
};

const UniversityProviderErrorUi = () => {
    return (
        <View h="100vh" minH="100vh">
            <View justifyContent="center" flex={1}>
                <Heading level="2" size="small" weight="bold" align="center" color="red600">
                    Error. Could not find university id or university slug.
                </Heading>
            </View>
        </View>
    );
};

type UseUniversityQueryProps = {
    universityId: string | null;
    universitySlug: string | null;
    skip: boolean;
};

type UseUniversityQueryResult = {
    data: GetUniversityByIdQuery | GetUniversityBySlugQuery | undefined;
    loading: boolean;
    error: ApolloError | undefined;
};

const useGetUniversityQuery = ({
    universityId,
    universitySlug,
    skip,
}: UseUniversityQueryProps): UseUniversityQueryResult => {
    const {
        data: dataFromId,
        loading: loadingFromId,
        error: errorFromId,
    } = useGetUniversityByIdQuery({
        variables: {universityId: universityId},
        skip: skip || !universityId,
    });
    const {
        data: dataFromSlug,
        loading: loadingFromSlug,
        error: errorFromSlug,
    } = useGetUniversityBySlugQuery({
        variables: {universitySlug: universitySlug},
        skip: skip || !universitySlug || !!universityId,
    });

    let data = dataFromId;
    let loading = loadingFromId;
    let error = errorFromId;
    if (!universityId) {
        data = dataFromSlug;
        loading = loadingFromSlug;
        error = errorFromSlug;
    }
    return {
        data,
        loading,
        error,
    };
};

export const UniversityProvider = ({
    universityId,
    universitySlug,
    children,
}: UniversityProviderProps) => {
    const urlParams = new URLSearchParams(window.location.search);
    const render = urlParams.get("render");
    const skip = render !== "chat" || (!universityId && !universitySlug);

    const {data, loading, error} = useGetUniversityQuery({universityId, universitySlug, skip});

    const value = useMemo(() => {
        return {
            universityId: universityId ?? data?.university?.id ?? "",
            universityName: universitySlug ?? data?.university?.name ?? "",
            universitySlug: universitySlug ?? data?.university?.slug ?? "",
            widgetUrl: data?.university?.widgetUrl ?? undefined,
        };
    }, [universityId, universitySlug, data]);

    if (error || (!universityId && !universitySlug)) {
        return <UniversityProviderErrorUi />;
    }

    if (loading) {
        return null;
    }

    return <UniversityContext.Provider value={value}>{children}</UniversityContext.Provider>;
};
